<template>
  <app-content class="h-panel">
    <div class="h-panel-bar">
      <span class="h-panel-title">科目汇总</span>
    </div>
    <div class="margin-right-left margin-top">
      <account-period-selector
          @confirm="handleConfirm"
          @date-change="handleDateChange"
          :current-subject="subject"
          :subject-list="subjectList"
      />
      <div class="float-right" style="display:flex;gap:12px;align-items:center;">
        <Button @click="exportExcel">导出</Button>
        <Checkbox v-model="showNumPrice">显示数量金额</Checkbox>
      </div>
    </div>
    <div class="h-panel-body" type="flex" :space-x="10">
      <!-- 外层容器：控制滚动 -->
      <div style="max-height: 670px; overflow-y: auto; width: 100%;">
        <Loading text="数据加载中..." :loading="loading"></Loading>

        <!-- 非数量金额表格：优化边框重叠效果 -->
        <table class="balance" v-if="!showNumPrice" style="border-collapse: separate; border-spacing: 0;">
          <thead class="header" style="position: sticky; top: 0; z-index: 1;">
          <tr>
            <td rowspan="2" v-width="100">科目编码</td>
            <td rowspan="2" v-width="200">科目名称</td>
            <td colspan="2">金额合计</td>
          </tr>
          <tr>
            <td>借方</td>
            <td>贷方</td>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in dataList" :key="item.subjecId">
            <td :style="{'padding-left':(item.level)*10+'px'}">{{item.code}}</td>
            <td :style="{'padding-left':(item.level)*10+'px'}"><span class="text-hover">{{item.name}}</span></td>
            <td align="right">{{item.currentDebitAmount|numFormat}}</td>
            <td align="right">{{item.currentCreditAmount|numFormat}}</td>
          </tr>
          <tr v-if="!dataList.length">
            <td colspan="4" class="text-center padding">暂无数据</td>
          </tr>
          </tbody>
        </table>

        <!-- 数量金额表格：优化边框重叠效果 -->
        <table class="balance" v-else style="border-collapse: separate; border-spacing: 0;">
          <thead class="header" style="position: sticky; top: 0; z-index: 1;">
          <tr>
            <td rowspan="2" v-width="100">科目编码</td>
            <td rowspan="2" v-width="200">科目名称</td>
            <td rowspan="2" v-width="100">计量单位</td>
            <td colspan="2">金额合计</td>
            <td colspan="2">数量合计</td>
          </tr>
          <tr>
            <td>借方</td>
            <td>贷方</td>
            <td>借方</td>
            <td>贷方</td>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in dataList" :key="item.subjecId">
            <td :style="{'padding-left':(item.level)*10+'px'}">{{item.code}}</td>
            <td :style="{'padding-left':(item.level)*10+'px'}"><span class="text-hover">{{item.name}}</span></td>
            <td class="text-center">{{item.unit || '-'}}</td>
            <td align="right">{{item.currentDebitAmount|numFormat}}</td>
            <td align="right">{{item.currentCreditAmount|numFormat}}</td>
            <td align="right">{{item.currentDebitAmountNum|numFormat}}</td>
            <td align="right">{{item.currentCreditAmountNum|numFormat}}</td>
          </tr>
          <tr v-if="!dataList.length">
            <td colspan="7" class="text-center padding">暂无数据</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </app-content>
</template>

<script>
import * as XLSX from "xlsx-js-style";
export default {
  name: "SubjectSummary",
  filters: {
    numFormat(val) {
      if (val === null || val === undefined || val === '') return '';
      const n = Number(val);
      return isNaN(n) ? '' : n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  },
  data() {
    return {
      dataList: [],
      accountDate: null,
      showNumPrice: false,
      numPriceDisabled: true,
      loading: false,
      subjectList: [],
      startDate: null,
      endDate: null,
      formattedStartDate: null,
      formattedEndDate: null,
      subject: null,
      subjectId: null,
      param: {
        keyName: 'id',
        parentName: 'parentId',
        titleName: 'subjectCodeName',
        dataMode: 'list',
        datas: []
      },
    }
  },
  watch: {
    subject() {
      let org = this.showNumPrice;
      if (this.subject && this.subject.unit) {
        this.numPriceDisabled = false;
        this.showNumPrice = true;
      } else {
        this.showNumPrice = false;
      }
      this.loadList();
    },
    showNumPrice() {
      this.loadList();
    }
  },
  methods: {
    formatDateToFirstDay(monthStr) {
      const [year, month] = monthStr.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      return `${year}-${String(month).padStart(2, '0')}-01`;
    },

    handleDateChange({ startMonth, endMonth }) {
      if (startMonth) {
        this.formattedStartDate = this.formatDateToFirstDay(startMonth);
        const [year, month] = startMonth.split('-').map(Number);
        this.startDate = new Date(year, month - 1, 1);
      }

      if (endMonth) {
        this.formattedEndDate = this.formatDateToFirstDay(endMonth);
        const [year, month] = endMonth.split('-').map(Number);
        this.endDate = new Date(year, month - 1, 1);
      }
    },
    handleConfirm(params) {
      this.formattedStartDate = this.formatDateToFirstDay(params.startMonth);
      this.formattedEndDate = this.formatDateToFirstDay(params.endMonth);

      if (params.subject) {
        this.subject = params.subject;
        this.subjectId = params.subject.id;
      } else {
        this.subject = null;
        this.subjectId = null;
      }
      this.loadList();
    },
    async loadSubject() {
      this.dataList = [];
      try {
        const { data } = await this.$api.accountbook.loadSubject({ accountDate: this.accountDate });
        const formattedData = data.map(subject => ({
          id: subject.id,
          subjectCode: subject.code,
          subjectName: subject.name,
          subjectCodeName: subject.code + " " + subject.name,
          subjectFullName: subject.subjectFullName || subject.name,
          ...subject
        }));
        this.subjectList = formattedData;
        this.$set(this.param, "datas", formattedData);
        if (formattedData.length) {
          this.subjectId = formattedData[0].id;
          this.subject = formattedData[0];
        }
      } catch (error) {
        console.error('加载科目失败:', error);
      }
    },
    loadList() {
      this.loading = true;
      this.$api.accountbook.loadSubjectSummaryByDate({
        startDate: this.formattedStartDate,
        endDate: this.formattedEndDate,
        subjectId: this.subjectId,
        showNumPrice: this.showNumPrice,
      }).then(({ data }) => {
        this.dataList = data;
      }).catch(() => {
      }).finally(() => {
        this.loading = false;
      });
    },

    // 导出Excel，与明细账/总账/科目余额表统一实现
    exportExcel() {
      if (!this.dataList || this.dataList.length === 0) {
        this.$Message.warning("暂无科目汇总数据，无法导出");
        return;
      }
      const sheetData = [];
      sheetData.push(["科目汇总"]);
      sheetData.push([]);

      if (!this.showNumPrice) {
        // 普通模式双层表头
        sheetData.push([
          "科目编码", "科目名称",
          "金额合计", ""
        ]);
        sheetData.push([
          "", "",
          "借方", "贷方"
        ]);

        this.dataList.forEach(item => {
          const nameIndent = "　".repeat(item.level || 0);
          sheetData.push([
            item.code || "",
            nameIndent + (item.name || ""),
            this.$options.filters.numFormat(item.currentDebitAmount),
            this.$options.filters.numFormat(item.currentCreditAmount)
          ]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
          { s: { r: 2, c: 2 }, e: { r: 2, c: 3 } }
        ];
        ws["!cols"] = [
          { wch: 14 }, { wch: 24 },
          { wch: 14 }, { wch: 14 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, "科目汇总");
        XLSX.writeFile(wb, `科目汇总_${this.formattedStartDate}_${this.formattedEndDate}.xlsx`);
      } else {
        // 数量金额双层表头
        sheetData.push([
          "科目编码", "科目名称", "计量单位",
          "金额合计", "",
          "数量合计", ""
        ]);
        sheetData.push([
          "", "", "",
          "借方", "贷方",
          "借方", "贷方"
        ]);

        this.dataList.forEach(item => {
          const nameIndent = "　".repeat(item.level || 0);
          sheetData.push([
            item.code || "",
            nameIndent + (item.name || ""),
            item.unit || "-",
            this.$options.filters.numFormat(item.currentDebitAmount),
            this.$options.filters.numFormat(item.currentCreditAmount),
            this.$options.filters.numFormat(item.currentDebitAmountNum),
            this.$options.filters.numFormat(item.currentCreditAmountNum)
          ]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
          { s: { r: 2, c: 3 }, e: { r: 2, c: 4 } },
          { s: { r: 2, c: 5 }, e: { r: 2, c: 6 } }
        ];
        ws["!cols"] = [
          { wch: 14 }, { wch: 24 }, { wch: 12 },
          { wch: 14 }, { wch: 14 },
          { wch: 12 }, { wch: 12 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, "科目汇总");
        XLSX.writeFile(wb, `科目汇总(数量金额)_${this.formattedStartDate}_${this.formattedEndDate}.xlsx`);
      }
      this.$Message.success("导出完成");
    }
  },
  created() {
    this.loadSubject();
  }
}
</script>

<style scoped lang="less">
.balance {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0; /* 消除单元格间隙，同时避免双重边框 */

  td {
    padding: 0 8px;
    border: 1px solid #e2e2e2;
    /* 关键优化：取消单元格右侧和底部边框，通过相邻单元格重叠实现单边框效果 */
    border-right: none;
    border-bottom: none;
    font-size: 12px;
    height: 32px;

    &.tip {
      background-color: #FFEEEF;
    }
  }

  /* 给最后一列恢复右侧边框 */
  tr td:last-child {
    border-right: 1px solid #e2e2e2;
  }

  /* 给最后一行恢复底部边框 */
  tbody tr:last-child td {
    border-bottom: 1px solid #e2e2e2;
  }

  tbody tr:nth-child(even) {
    background-color: #f8fbf8;
  }

  tbody tr:hover {
    background-color: #F0F6FF;
  }

  .header {
    td {
      background-color: #F5F5F5;
      text-align: center;
      font-weight: bold;
      height: 35px;
      /* 表头最后一列恢复右侧边框 */
      &:last-child {
        border-right: 1px solid #e2e2e2;
      }
    }
  }
}

.text-center {
  text-align: center;
}
.padding {
  padding: 10px 0;
}
.text-hover {
  cursor: pointer;
}
.float-right {
  float: right;
}
.margin-right-left {
  margin: 0 10px;
}
.margin-top {
  margin-top: 10px;
}
</style>